import React, { useState, useEffect } from 'react';
import { InformationCircleIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import FormInput from '../ui/FormInput';

/**
 * Component for collecting parcel dimensions and weight for shipping calculations
 */
const ParcelInfoInput = ({ 
  parcelInfo, 
  onChange, 
  errors = {}, 
  includePresets = true,
  region = 'US' // Default to US
}) => {
  // Track which preset is selected
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [currentRegion, setCurrentRegion] = useState(region);
  
  // Common package presets for easy selection, organized by region
  const packagePresetsMap = {
    US: [
      { 
        id: 'small-envelope-us',
        name: 'Small Envelope', 
        description: 'For jewelry, accessories, small flat items',
        dimensions: { length: 9, width: 6, height: 0.25 },
        weight: 0.5,
        units: { distance: 'in', mass: 'lb' }
      },
      { 
        id: 'poly-mailer-us',
        name: 'Poly Mailer', 
        description: 'For clothing, soft items',
        dimensions: { length: 12, width: 9, height: 1 },
        weight: 1.0,
        units: { distance: 'in', mass: 'lb' }
      },
      { 
        id: 'small-box-us',
        name: 'Small Box', 
        description: 'For shoes, folded clothes, small items',
        dimensions: { length: 12, width: 10, height: 5 },
        weight: 2.0,
        units: { distance: 'in', mass: 'lb' }
      },
      { 
        id: 'medium-box-us',
        name: 'Medium Box', 
        description: 'For multiple clothing items, medium sized items',
        dimensions: { length: 14, width: 12, height: 7 },
        weight: 3.0,
        units: { distance: 'in', mass: 'lb' }
      },
      { 
        id: 'large-box-us',
        name: 'Large Box', 
        description: 'For larger items, multiple products',
        dimensions: { length: 18, width: 16, height: 10 },
        weight: 5.0,
        units: { distance: 'in', mass: 'lb' }
      }
    ],
    international: [
      { 
        id: 'small-envelope-intl',
        name: 'Small Envelope', 
        description: 'For jewelry, accessories, small flat items',
        dimensions: { length: 23, width: 15, height: 0.5 },
        weight: 0.25,
        units: { distance: 'cm', mass: 'kg' }
      },
      { 
        id: 'poly-mailer-intl',
        name: 'Poly Mailer', 
        description: 'For clothing, soft items',
        dimensions: { length: 30, width: 23, height: 2.5 },
        weight: 0.5,
        units: { distance: 'cm', mass: 'kg' }
      },
      { 
        id: 'small-box-intl',
        name: 'Small Box', 
        description: 'For shoes, folded clothes, small items',
        dimensions: { length: 30, width: 25, height: 15 },
        weight: 1.0,
        units: { distance: 'cm', mass: 'kg' }
      },
      { 
        id: 'medium-box-intl',
        name: 'Medium Box', 
        description: 'For multiple clothing items, medium sized items',
        dimensions: { length: 35, width: 30, height: 20 },
        weight: 1.5,
        units: { distance: 'cm', mass: 'kg' }
      },
      { 
        id: 'large-box-intl',
        name: 'Large Box', 
        description: 'For larger items, multiple products',
        dimensions: { length: 45, width: 40, height: 25 },
        weight: 2.5,
        units: { distance: 'cm', mass: 'kg' }
      }
    ]
  };
  
  // Update region when prop changes
  useEffect(() => {
    if (region !== currentRegion) {
      setCurrentRegion(region);
    }
  }, [region, currentRegion]);
  
  // Get the current package presets based on region
  const packagePresets = packagePresetsMap[currentRegion === 'US' ? 'US' : 'international'] || packagePresetsMap.US;
  
  // Get the appropriate units for the region
  const getUnits = () => {
    return currentRegion === 'US' 
      ? { distance: 'in', mass: 'lb' } 
      : { distance: 'cm', mass: 'kg' };
  };

  // Get combined data - check both the component and JSON version
  const getCombinedData = () => {
    let combinedData = {};
    
    console.log('ParcelInfoInput - Initial parcelInfo:', parcelInfo);
    
    // If parcelInfo is a string (JSON from backend), parse it
    if (typeof parcelInfo === 'string') {
      try {
        combinedData = JSON.parse(parcelInfo);
        console.log('ParcelInfoInput - Parsed JSON data:', combinedData);
      } catch (e) {
        console.error('Failed to parse parcelInfo JSON:', e);
      }
    } else if (parcelInfo) {
      // Otherwise use the object directly
      combinedData = { ...parcelInfo };
      console.log('ParcelInfoInput - Using object data directly:', combinedData);
    }
    
    // If parcelInfo is an object with a nested parcelInfoData structure
    if (parcelInfo && parcelInfo.parcelInfoData) {
      console.log('ParcelInfoInput - Found nested parcelInfoData:', parcelInfo.parcelInfoData);
      if (!combinedData.length && parcelInfo.parcelInfoData.length) {
        combinedData.length = parcelInfo.parcelInfoData.length;
      }
      if (!combinedData.width && parcelInfo.parcelInfoData.width) {
        combinedData.width = parcelInfo.parcelInfoData.width;
      }
      if (!combinedData.height && parcelInfo.parcelInfoData.height) {
        combinedData.height = parcelInfo.parcelInfoData.height;
      }
      if (!combinedData.weight && parcelInfo.parcelInfoData.weight) {
        combinedData.weight = parcelInfo.parcelInfoData.weight;
      }
      // Get units info if available
      if (!combinedData.units && parcelInfo.parcelInfoData.units) {
        combinedData.units = parcelInfo.parcelInfoData.units;
      }
    }
    
    // If we have dimensions but no units info, add default units based on current region
    if (combinedData.length !== undefined && !combinedData.units) {
      combinedData.units = getUnits();
      console.log('ParcelInfoInput - No units found, using default:', combinedData.units);
    }
    
    // Convert string values to numbers for comparison with presets
    if (combinedData.length) combinedData.length = parseFloat(combinedData.length);
    if (combinedData.width) combinedData.width = parseFloat(combinedData.width);
    if (combinedData.height) combinedData.height = parseFloat(combinedData.height);
    if (combinedData.weight) combinedData.weight = parseFloat(combinedData.weight);
    
    console.log('ParcelInfoInput - Final combined data:', combinedData);
    return combinedData;
  };
  
  // Get the actual parcel data to display
  const parcelData = getCombinedData();

  // Check if the current values match any preset
  useEffect(() => {
    if (!parcelData) return;
    
    // Convert all values to numbers for comparison
    const length = parseFloat(parcelData.length);
    const width = parseFloat(parcelData.width);
    const height = parseFloat(parcelData.height);
    const weight = parseFloat(parcelData.weight);
    
    // Only proceed if we have valid numbers
    if (isNaN(length) || isNaN(width) || isNaN(height) || isNaN(weight)) {
      setSelectedPreset(null);
      return;
    }
    
    console.log('Checking for matching preset with values:', { length, width, height, weight });
    
    // Find preset with a small tolerance for floating point comparison
    const matchingPreset = packagePresets.find(preset => {
      // Allow for small differences in floating point values (0.1 tolerance)
      const lengthMatch = Math.abs(preset.dimensions.length - length) < 0.1;
      const widthMatch = Math.abs(preset.dimensions.width - width) < 0.1;
      const heightMatch = Math.abs(preset.dimensions.height - height) < 0.1;
      const weightMatch = Math.abs(preset.weight - weight) < 0.1;
      
      return lengthMatch && widthMatch && heightMatch && weightMatch;
    });
    
    if (matchingPreset) {
      console.log('Found matching preset:', matchingPreset.id);
    }
    
    setSelectedPreset(matchingPreset ? matchingPreset.id : null);
  }, [parcelData, packagePresets]);
  
  // Update dimensions when region changes
  useEffect(() => {
    // Clear selected preset when region changes
    setSelectedPreset(null);
    
    // Only convert if we have data and either no units info or units don't match current region
    const needsConversion = parcelData && 
      parcelData.length !== undefined && 
      parcelData.width !== undefined && 
      parcelData.height !== undefined && 
      parcelData.weight !== undefined &&
      (!parcelData.units || 
        (currentRegion === 'US' && parcelData.units.distance !== 'in') ||
        (currentRegion !== 'US' && parcelData.units.distance !== 'cm'));
    
    if (needsConversion) {
      console.log('Converting dimensions due to region change');
      const convertedInfo = convertDimensions(parcelData, currentRegion);
      onChange(convertedInfo);
    }
  }, [currentRegion]);

  // Convert dimensions between unit systems
  const convertDimensions = (info, targetRegion) => {
    // If no conversion needed or empty values, return as is
    if (!info || 
        info.length === undefined || 
        info.width === undefined || 
        info.height === undefined || 
        info.weight === undefined) {
      return info;
    }
    
    // Check if conversion is actually needed based on units
    if (info.units) {
      const currentUnitSystem = info.units.distance;
      const targetUnitSystem = targetRegion === 'US' ? 'in' : 'cm';
      
      // If units already match target region, no conversion needed
      if (currentUnitSystem === targetUnitSystem) {
        console.log('No conversion needed, units already match:', currentUnitSystem);
        return info;
      }
    }
    
    // Convert from US to international (inches/lb to cm/kg)
    if (targetRegion !== 'US') {
      return {
        ...info,
        length: (parseFloat(info.length) * 2.54).toFixed(1),
        width: (parseFloat(info.width) * 2.54).toFixed(1), 
        height: (parseFloat(info.height) * 2.54).toFixed(1),
        weight: (parseFloat(info.weight) * 0.453592).toFixed(2),
        units: { distance: 'cm', mass: 'kg' }
      };
    }
    
    // Convert from international to US (cm/kg to inches/lb)
    if (targetRegion === 'US') {
      return {
        ...info,
        length: (parseFloat(info.length) / 2.54).toFixed(1),
        width: (parseFloat(info.width) / 2.54).toFixed(1),
        height: (parseFloat(info.height) / 2.54).toFixed(1),
        weight: (parseFloat(info.weight) / 0.453592).toFixed(2),
        units: { distance: 'in', mass: 'lb' }
      };
    }
    
    return info;
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    onChange({
      ...parcelData,
      length: preset.dimensions.length,
      width: preset.dimensions.width,
      height: preset.dimensions.height,
      weight: preset.weight,
      units: preset.units
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log(`ParcelInfoInput - Before update - ${name}:`, value);
    console.log('ParcelInfoInput - Current state:', parcelData);
    
    // Ensure value is valid number or empty string
    let parsedValue = value;
    if (value !== '') {
      // Allow only numeric input with decimal point
      if (!/^[0-9]*\.?[0-9]*$/.test(value)) {
        return;
      }
      // Don't update if the value is just a decimal point
      if (value === '.') {
        parsedValue = '0.';
      }
    }
    
    const updatedParcelInfo = {
      ...parcelData,
      [name]: parsedValue,
      units: getUnits()
    };
    
    console.log(`ParcelInfoInput - After update - Full parcel info:`, updatedParcelInfo);
    
    // Clear selected preset when manual input changes
    setSelectedPreset(null);
    
    onChange(updatedParcelInfo);
  };

  const units = getUnits();

  return (
    <div>
      {/* Units indicator based on shipping address country */}
      {/* <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          <GlobeAltIcon className="h-5 w-5 mr-1" />
          Measurement Units
        </label>
        <div className="max-w-xs">
          <FormInput
            as="select"
            name="region"
            value={selectedRegion}
            onChange={handleRegionChange}
            options={[
              { value: 'US', text: 'US Units (inches, pounds)' },
              { value: 'international', text: 'Metric Units (centimeters, kilograms)' }
            ]}
          />
        </div>
      </div> */}
    
      {/* Package size presets */}
      {includePresets && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Common Package Sizes
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {packagePresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handlePresetSelect(preset)}
                className={`border rounded-md p-2 text-left hover:bg-gray-50 transition-colors text-sm 
                  ${selectedPreset === preset.id 
                    ? 'border-brand-teal bg-form-bg ring-1 ring-brand-teal' 
                    : 'border-gray-300 hover:border-gray-400'}`}
              >
                <h4 className={`font-medium ${selectedPreset === preset.id ? 'text-brand-teal-dark' : ''}`}>
                  {preset.name}
                </h4>
                <p className="text-xs text-gray-500 mt-1 line-clamp-1">{preset.description}</p>
                <div className="mt-1 text-xs text-gray-600">
                  <span>
                    {preset.dimensions.length} × {preset.dimensions.width} × {preset.dimensions.height} {units.distance} | {preset.weight} {units.mass}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dimensions and weight inputs in a single row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="col-span-1 md:col-span-1">
          <FormInput
            label={`Length (${units.distance})`}
            name="length"
            type="text"
            value={(parcelData.length !== undefined && !isNaN(parcelData.length)) ? parcelData.length : ''}
            onChange={handleInputChange}
            error={errors.length}
            placeholder="0.0"
          />
        </div>
        
        <div className="col-span-1 md:col-span-1">
          <FormInput
            label={`Width (${units.distance})`}
            name="width"
            type="text"
            value={(parcelData.width !== undefined && !isNaN(parcelData.width)) ? parcelData.width : ''}
            onChange={handleInputChange}
            error={errors.width}
            placeholder="0.0"
          />
        </div>
        
        <div className="col-span-1 md:col-span-1">
          <FormInput
            label={`Height (${units.distance})`}
            name="height"
            type="text"
            value={(parcelData.height !== undefined && !isNaN(parcelData.height)) ? parcelData.height : ''}
            onChange={handleInputChange}
            error={errors.height}
            placeholder="0.0"
          />
        </div>
        
        <div className="col-span-1 md:col-span-1">
          <FormInput
            label={`Weight (${units.mass})`}
            name="weight"
            type="text"
            value={(parcelData.weight !== undefined && !isNaN(parcelData.weight)) ? parcelData.weight : ''}
            onChange={handleInputChange}
            error={errors.weight}
            placeholder="0.0"
          />
        </div>
      </div>
    </div>
  );
};

export default ParcelInfoInput;